import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiDelete, adminCompanyPath } from '../../lib/api-client';
import { useCompany } from '../../contexts/CompanyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    options.push({ value, label });
  }
  return options;
}

function padDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getMonthRange(yearMonth: string): { dateFrom: string; dateTo: string; syncDate: string } {
  const [year, month] = yearMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0);
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const endDate = isCurrentMonth ? now : lastDay;
  return {
    dateFrom: `${yearMonth}-01`,
    dateTo: padDate(endDate),
    // pgway operates in GMT+7. Send month start in their TZ as UTC.
    syncDate: new Date(`${yearMonth}-01T00:00:00.000+07:00`).toISOString(),
  };
}

export function SyncManager() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { activeCompanyId } = useCompany();
  const [sellerSyncResult, setSellerSyncResult] = useState<string | null>(null);
  const [statSyncResult, setStatSyncResult] = useState<string | null>(null);
  const [cleanupResult, setCleanupResult] = useState<string | null>(null);

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const monthOptions = getMonthOptions();
  const { dateFrom, dateTo, syncDate } = getMonthRange(selectedMonth);
  const monthLabel = monthOptions.find((o) => o.value === selectedMonth)?.label ?? selectedMonth;

  const syncSellersMutation = useMutation({
    mutationFn: () =>
      apiPost<{ message: string }>(adminCompanyPath('/sync/sellers', activeCompanyId!)),
    onSuccess: (res) => {
      if (res.ok) {
        setSellerSyncResult(JSON.stringify(res.data, null, 2));
        queryClient.invalidateQueries({ queryKey: ['admin', 'sellers'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'api-config'] });
      } else {
        setSellerSyncResult(res.error.message);
      }
    },
    onError: (err) => {
      setSellerSyncResult((err as Error).message);
    },
  });

  const cleanupStatsMutation = useMutation({
    mutationFn: () =>
      apiDelete<{ deletedCount: number }>(adminCompanyPath('/sync/stats', activeCompanyId!), {
        dateFrom,
        dateTo,
      }),
    onSuccess: (res) => {
      if (res.ok) {
        setCleanupResult(`Deleted ${res.data.deletedCount} records for ${monthLabel}.`);
        queryClient.invalidateQueries({ queryKey: ['admin', 'seller-stats'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'merchant-stats'] });
        queryClient.invalidateQueries({ queryKey: ['seller', 'stats'] });
        queryClient.invalidateQueries({ queryKey: ['sub-seller', 'stats'] });
      } else {
        setCleanupResult(res.error.message);
      }
    },
    onError: (err) => {
      setCleanupResult((err as Error).message);
    },
  });

  const syncStatsMutation = useMutation({
    mutationFn: () =>
      apiPost<{ message: string }>(adminCompanyPath('/sync/stats', activeCompanyId!), {
        queryParams: { date: syncDate, timeRange: 'monthly' },
      }),
    onSuccess: (res) => {
      if (res.ok) {
        setStatSyncResult(JSON.stringify(res.data, null, 2));
      } else {
        setStatSyncResult(res.error.message);
      }
    },
    onError: (err) => {
      setStatSyncResult((err as Error).message);
    },
  });

  if (!activeCompanyId) {
    return (
      <div className="space-y-6">
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('company.noCompanySelected')}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-300">
            {t('admin.targetMonth')}
          </label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {dateFrom} ~ {dateTo}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.syncSellers')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.syncSellersDesc')}</p>
            <Button
              onClick={() => {
                setSellerSyncResult(null);
                syncSellersMutation.mutate();
              }}
              disabled={syncSellersMutation.isPending}
            >
              {syncSellersMutation.isPending ? t('common.loading') : t('admin.syncNow')}
            </Button>
            {sellerSyncResult && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">
                {sellerSyncResult}
              </pre>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('admin.syncStats')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('admin.syncStatsDescScoped', { month: monthLabel })}
            </p>
            <Button
              onClick={() => {
                setStatSyncResult(null);
                syncStatsMutation.mutate();
              }}
              disabled={syncStatsMutation.isPending}
            >
              {syncStatsMutation.isPending ? t('common.loading') : t('admin.syncNow')}
            </Button>
            {statSyncResult && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">
                {statSyncResult}
              </pre>
            )}
          </CardContent>
        </Card>

        <Card defaultCollapsed>
          <CardHeader>
            <CardTitle>{t('admin.cleanupStats')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('admin.cleanupStatsDescScoped', { month: monthLabel })}
            </p>
            <Button
              variant="outline"
              className="text-red-600 dark:text-red-400"
              onClick={() => {
                if (window.confirm(t('admin.cleanupConfirmScoped', { month: monthLabel }))) {
                  setCleanupResult(null);
                  cleanupStatsMutation.mutate();
                }
              }}
              disabled={cleanupStatsMutation.isPending}
            >
              {cleanupStatsMutation.isPending ? t('common.loading') : t('admin.cleanupNow')}
            </Button>
            {cleanupResult && (
              <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-900 dark:text-gray-300">
                {cleanupResult}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
